/**
 * Онбординг: заводим бренд по его сайту.
 *
 * Путь клиента (спека §4) начинается с одного поля: человек вводит адрес сайта,
 * а всё остальное — название, города, синонимы названия, конкурентов и сами
 * вопросы — продукт определяет сам, разобрав главную страницу. Поэтому здесь
 * два шага, а не анкета: **сайт** и **подтверждение того, что мы нашли**.
 *
 * Отрасли в этом мастере нет. Она осталась внутренней меткой разбора: клиента
 * про неё не спрашивают, ему её не показывают и в запросах кабинета её нет.
 *
 * Четыре вещи, которые в этом экране важнее вёрстки.
 *
 * 1. **Разбор сайта — предложение, а не запись.** `POST /brands/detect` ничего
 *    не сохраняет, и человек правит предложенное до того, как оно станет
 *    брендом.
 * 2. **Сайт может не открыться.** Тогда экран не упирается в ошибку, а
 *    предлагает заполнить профиль руками — иначе клиент, у которого лежит сайт,
 *    не может начать работу вовсе.
 * 3. **Ядро приходит не сразу.** `POST /brands/bootstrap` отвечает до того, как
 *    появится хоть один вопрос: разбор сайта и вызовы модели занимают до
 *    минуты. Поэтому конец мастера — не сводка «собрано N вопросов», а
 *    ожидание с опросом `GET /brands/{brandId}`.
 * 4. **Тариф не запирает форму.** Завести бренд можно без подписки — оплата
 *    нужна ядру и замеру, и о ней экран говорит заметкой над формой. Отсюда два
 *    пути заведения: `POST /brands/bootstrap` (бренд с ядром, сервер откажет без
 *    свободных вопросов) и `POST /brands` (один бренд, оплатой не гейтится).
 * 5. **Заготовка не выдаётся за ответ.** Пока `POST /brands/detect` сайт не
 *    читает (`site_profile.site_read` — `false`), ни имени, ни городов у него
 *    нет: имя он выводит из адреса, города отдаёт пустыми. Подставленное в поле,
 *    такое имя выглядит ответом продукта — человек его подтверждает, и с этой
 *    минуты оно названо человеком, а названное человеком разбор сайта не трогает
 *    никогда. Поэтому при заготовке поля остаются пустыми, а подписи говорят,
 *    что название и города возьмутся с сайта.
 *
 * Синонимы и конкуренты показаны как факт разбора. Синонимы заводятся вместе с
 * брендом обоими путями, конкуренты — только через `bootstrap`. Править их можно
 * сразу после — в карточке бренда, и туда экран и уводит.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, idempotencyKey } from '../../api/client';
import { ApiError, fieldErrors } from '../../api/problem';
import { useAccess } from '../../session';
import { useScope } from '../../scope';
import { useLoad } from '../../ui/load';
import { useScrollToTop } from '../../ui/scroll';
import { Chips, chipsValue, SelectField, TextField } from '../../ui/fields';
import { Empty, ErrorNotice, Pipeline, Skeleton } from '../../ui/state';
import {
  isPreparing,
  prepareSteps,
  PreparationFailed,
  Preparing,
  rebuildQuestions,
  useRebuildAttempt,
  usePreparation,
} from '../../ui/preparation';
import { aliasKind, nameSource, parentAliasNote } from '../../ui/labels';
import { normalizeDomain } from '@seeno/shared/domain';
import { countOf, coverageOf, domainProse, int } from '@shared/src/format';
import type { components } from '../../api/schema';
import { scanBlockedFallback } from '../../ui/screen-states';
import { CORE_SIZE_RULE, ORGANIC_RULE } from '@shared/src/product';

type Proposal = components['schemas']['BrandProfileProposal'];
type Brand = components['schemas']['Brand'];
type BrandAlias = components['schemas']['BrandAlias'];
type BootstrapResult = components['schemas']['BrandBootstrapResult'];
type Quotas = components['schemas']['OrganizationQuotas'];

/**
 * Читали ли мы сайт на самом деле.
 *
 * `POST /brands/detect` возвращает `site_profile.site_read`. Сейчас там `false`
 * всегда: разбор детерминированный, без внешнего вызова — название берётся
 * первой меткой домена, города и конкуренты пусты. От этого признака зависят и
 * подписи шага, и то, что мастер вообще предлагает как найденное; когда разбор
 * станет настоящим, оба выправятся сами.
 */
function siteWasRead(proposal: Proposal | null): boolean {
  return Boolean(
    (proposal?.site_profile as { site_read?: boolean } | undefined)?.site_read,
  );
}

/**
 * Заглушка названия по адресу сайта.
 *
 * Нужна ровно одному пути — `POST /brands`, где имя обязательно по контракту, а
 * подготовки, которая прочитала бы сайт и назвала бренд, на этом пути нет
 * вовсе. Во всём остальном пустое поле уходит пустым: заглушку выводит сервер и
 * помечает её заглушкой, а помеченную заглушку разбор сайта вправе заменить.
 *
 * Считается так же, как на сервере: домен приводится к читаемому виду
 * (`domainProse` разбирает punycode), у него берётся первая метка без `www`.
 * Из `xn--h1aen.xn--p1ai` иначе вышло бы «Xn--h1aen».
 */
export function nameFromDomain(domain: string): string {
  const readable = domainProse(normalizeDomain(domain)).replace(/^www\./, '');
  const label = readable.split('.', 1)[0] ?? '';
  return label === '' ? '' : label.slice(0, 1).toUpperCase() + label.slice(1);
}

/**
 * Что вышло из заведения.
 *
 * Путей два, и они дают разное. `POST /brands/bootstrap` заводит бренд и ставит
 * его в очередь подготовки, но упирается в тариф: сервер отвечает `402`, если
 * действующего тарифа нет вовсе, и `409`, если свободных вопросов не осталось, —
 * причём **до** того, как что-нибудь создаст. `POST /brands` тарифом не гейтится
 * вообще. Поэтому организация без подписки заводит бренд вторым путём и добирает
 * вопросы после оплаты, а не упирается в стену на первом же экране.
 */
type Created =
  { kind: 'core'; result: BootstrapResult } | { kind: 'brand'; brand: Brand };

/**
 * В какое пространство бренд попадёт, если человек ничего не менял.
 *
 * Здесь стоял `workspaces[0]`, и для агентства это была тихая порча чужих
 * данных. Порядок в списке — не ответ человека на вопрос «чей это бренд»:
 * агентство, только что заведшее клиента и переключившееся на него, заводило
 * его первый бренд **в чужое пространство** — то, что оказалось в списке
 * первым. Бренд уезжал к постороннему клиенту, вопросы съедали его выделенную
 * квоту, и заметить это можно было только по чужой карточке. Селектор при
 * одном пространстве не показывается вовсе, поэтому и поправить было нечем.
 *
 * Дефолт — выбранное в области пространство: переключатель контекста наверху и
 * есть тот самый ответ. Существование проверяется — сохранённый выбор мог
 * указывать на пространство, которого в организации уже нет.
 */
export function defaultWorkspaceId(
  workspaces: readonly { id: string }[],
  chosen: string | null,
): string {
  return workspaces.find((w) => w.id === chosen)?.id ?? workspaces[0]?.id ?? '';
}

/** Что заполнено на втором шаге. Предложение разбора уже слито с правками. */
interface Draft {
  name: string;
  domain: string;
  regions: string[];
  workspaceId: string;
  questionsTarget: string;
}

/**
 * Шаг мастера. Предложение разбора и результат заведения живут отдельными
 * состояниями: на шаге «заводим» предложение всё ещё показано справа, и прятать
 * его на время запроса значило бы моргать половиной экрана.
 */
type Stage = 'domain' | 'detecting' | 'profile' | 'creating' | 'done';

export function Onboarding({ first = false }: { first?: boolean }) {
  const scope = useScope();
  const access = useAccess();
  const navigate = useNavigate();

  const quotas = useLoad<Quotas | null>('quotas', (signal) =>
    access.reaches('GET', '/organizations/current/quotas')
      ? api.get<Quotas>('/organizations/current/quotas', { signal })
      : Promise.resolve(null),
  );

  const [stage, setStage] = useState<Stage>('domain');
  const [domain, setDomain] = useState('');
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  // Недовведённые слова чип-полей: их держит форма, а не сами поля, иначе
  // «Завести бренд» теряет вписанное и не подтверждённое Enter-ом слово.
  const [regionDraft, setRegionDraft] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  /* Тронул ли человек поле имени.
     Отличает заготовку по адресу от названия, которое человек признал своим.
     Нетронутая заготовка в запрос НЕ уезжает, и бренд заводится с именем
     происхождения `domain`, которое разбор сайта заменит прочитанным. Судить
     по совпадению строк нельзя: человек вправе вписать ровно ту же строку, и
     тогда это уже его решение, а не наша догадка. */
  const [nameTouched, setNameTouched] = useState(false);

  const [created, setCreated] = useState<Created | null>(null);
  const [error, setError] = useState<unknown>(null);
  /* Бренды, известные области ПЕРЕД попыткой заведения. Смотри `madeAnyway`. */
  const knownBefore = useRef<Set<string>>(new Set());
  /* Подготовка договорила: шапка обязана перестать писать «Готовим вопросы»,
     когда экран показывает готовое. Держит это мастер, а не сам экран
     ожидания, — шапка его. */
  const [prepared, setPrepared] = useState(false);

  // Шаги разной высоты, а адрес один: без этого после подтверждения человек
  // остаётся посреди страницы, где заголовок уже уехал вверх.
  useScrollToTop(stage);

  const workspaces = scope.state.kind === 'ready' ? scope.state.workspaces : [];
  const chosenWorkspace = defaultWorkspaceId(workspaces, scope.workspaceId);
  const quota = quotas.state.kind === 'ready' ? quotas.state.data : null;
  const free = quota
    ? Math.max(0, quota.tracked_questions - (quota.questions_used ?? 0))
    : null;

  /**
   * Соберётся ли ядро вместе с брендом.
   *
   * Ровно тот предикат, по которому откажет сервер: `ensure_bootstrap_capacity`
   * валит запрос, если строки квоты нет (`402`) или свободных вопросов ноль
   * (`409`). Свободные вопросы бывают только у действующего тарифа, поэтому
   * `free > 0` покрывает оба случая.
   *
   * `null` при **успешно** прочитанной квоте означает ровно одно: роль не
   * читает квоты — запрос тогда и не уходит, `useLoad` отдаёт `null` успехом.
   * Решает сервер: он ответит `402`/`409`, и это честнее догадки экрана.
   *
   * Незавершённая и упавшая загрузки — это НЕ «ограничений нет», и путь по ним
   * не выбирается вовсе: см. `quotaKnown`.
   *
   * Проверка НЕ решает, показывать ли форму. Она решает, каким запросом
   * заводить бренд.
   */
  const canBuildCore = free === null || free > 0;

  const siteRead = siteWasRead(proposal);

  /**
   * Название и города берутся с сайта, а не с человека.
   *
   * Так на заготовке: разбор состоялся, но сайт не читал, и всё, что он вернул,
   * выведено из адреса. Поля остаются пустыми и необязательными — пустое имя
   * уходит в `POST /brands/bootstrap` без поля `name`, и сервер сам выводит
   * заглушку и помечает её заглушкой. Вписанное же человеком имя принадлежит
   * человеку: разбор сайта не тронет его никогда, и предложить ему подтвердить
   * метку домена значит запереть бренд под именем «Asc-exeed» навсегда.
   *
   * `canBuildCore` в условии — не перестраховка. Сайт читает ПОДГОТОВКА, а её
   * ставит только `POST /brands/bootstrap`; когда свободных вопросов у тарифа
   * нет, бренд заводится вторым путём, и сайт не прочитает никто. Обещание
   * «возьмём с сайта» на этом пути — обещание, которое некому исполнить, и имя
   * с городами приходится спросить у человека, как и раньше.
   */
  const siteWillTell = proposal !== null && !siteRead && canBuildCore;

  /**
   * Известна ли квота настолько, чтобы выбирать путь заведения.
   *
   * Пока `/organizations/current/quotas` не ответил, `quota` равна `null` — и
   * «ещё не знаем» читалось как «ограничений нет»: организация без тарифа
   * уезжала в `/brands/bootstrap` и ловила `402`. Тот самый случай, ради
   * которого гейт и снимался, открывался заново через гонку загрузки.
   *
   * Откатиться после отказа нельзя: `ensure_bootstrap_capacity` отсекает до
   * создания, но постановка в очередь подготовки — уже после того, как
   * `create_brand` закоммитил бренд своей транзакцией, а тексты у обоих отказов
   * одинаковые. Отличить «ничего не создано» от «бренд уже есть» по ответу
   * невозможно, поэтому повтор другим запросом завёл бы дубль.
   *
   * Отсюда правило: форму это состояние не закрывает — первый шаг и разбор
   * сайта от тарифа не зависят вовсе, — но кнопка заведения ждёт ответа.
   */
  const quotaKnown = quotas.state.kind === 'ready';
  const canPay = access.reaches('POST', '/billing/subscription/plan');

  /* Заготовка полей, зависящих от тарифа. Одна на оба входа в шаг 2, чтобы
     разбор сайта и заполнение руками не разошлись значениями по умолчанию.
     Ноль в поле «сколько вопросов отслеживать» выглядел бы поломкой, а не
     состоянием тарифа, поэтому при пустой квоте берётся обычные 50: само поле
     при этом не показывается, а причину называет заметка. */
  const targetDefault = String(free !== null && free > 0 ? Math.min(50, free) : 50);

  const problem = error instanceof ApiError ? error.problem : null;
  const fields = problem ? fieldErrors(problem) : {};

  /**
   * Бренд с этим адресом, который уже есть в области.
   *
   * `bootstrap` не транзакционен: `create_brand` коммитит бренд своей
   * транзакцией, и отказ на постановке подготовки оставляет бренд ЗАВЕДЁННЫМ.
   * Экран после отказа показывал прежнюю кнопку, человек жал её второй раз —
   * и получал либо занятое имя, либо, если правил название между попытками,
   * второй бренд на тот же сайт. Ключ идемпотентности от этого не спасает: на
   * любом не-2xx сервер его освобождает, и повтор с тем же ключом проходит как
   * новый запрос.
   *
   * Поэтому после отказа область перечитывается, и найденный бренд предлагается
   * ОТКРЫТЬ, а не заводить заново.
   *
   * Ищется только бренд, КОТОРОГО НЕ БЫЛО перед попыткой: снимок известных
   * идентификаторов снимается на отправке. Иначе отказ по тарифу, случившийся
   * до всякого создания, показывал бы «откройте заведённый» на бренде, который
   * человек завёл месяц назад на общий сайт сети, — и предлагал бы открыть
   * чужую карточку вместо объяснения отказа.
   */
  const madeAnyway =
    error !== null && draft
      ? (scope.liveBrands.find(
          (item) =>
            !knownBefore.current.has(item.id) &&
            item.workspace_id === draft.workspaceId &&
            normalizeDomain(item.domain ?? '') === normalizeDomain(draft.domain),
        ) ?? null)
      : null;

  const head = (
    <div className="screen-head">
      <h1>{first ? 'Заведём первый бренд' : 'Новый бренд'}</h1>
      <p className="stamp">
        {stage === 'domain' || stage === 'detecting'
          ? 'Шаг 1 из 2 · сайт бренда'
          : stage === 'done'
            ? created?.kind === 'core' && !prepared
              ? 'Готовим вопросы'
              : 'Готово'
            : siteRead
              ? 'Шаг 2 из 2 · что мы нашли'
              : 'Шаг 2 из 2 · заготовка по адресу'}
      </p>
    </div>
  );

  // Заводить бренды может не всякая роль. Раздел скрыт матрицей контракта, но
  // прямая ссылка сюда всё равно возможна — и она честно отвечает, а не 403.
  if (!access.reaches('POST', '/brands')) {
    return (
      <>
        {head}
        <Empty eyebrow="Не ваша роль" title="Бренды заводит владелец или аналитик">
          Ваша роль открывает кабинет на просмотр и выгрузку. Попросите завести бренд
          того, кто ведёт замер в этой организации.
        </Empty>
      </>
    );
  }

  if (scope.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={2} />
      </>
    );
  }

  /* Стены «сначала оплатите» здесь больше нет, и это не смягчение правила, а
     исправление ошибки. Условие `!quota.scan_allowed || free === 0` истинно у
     ЛЮБОЙ только что заведённой организации: тарифа у неё ещё нет, значит
     `scan_allowed` — `false`, а `tracked_questions − questions_used` — это
     `0 − 0`. Оба дизъюнкта срабатывали всегда, и ранний возврат отдавал новому
     клиенту экран «Заведём первый бренд · Шаг 1 из 2» без единого поля и с одной
     кнопкой «Открыть тариф». Завести бренд не мог никто и никогда — при том что
     `POST /brands` оплатой не гейтится ни на сервере, ни в контракте.

     Тариф теперь называется заметкой над формой (`PlanNotice`), как на экране
     тарифа, а не стеной вместо формы. */

  async function detect() {
    // Приведение ещё раз здесь, а не только на уходе из поля: отправить форму
    // можно клавишей Enter, ни разу поле не покинув.
    const clean = normalizeDomain(domain);
    setDomain(clean);
    setError(null);
    setStage('detecting');
    try {
      const found = await api.post<Proposal>(
        '/brands/detect',
        { domain: clean, ...(name.trim() ? { name: name.trim() } : {}) },
        { idempotencyKey: idempotencyKey() },
      );
      /* Поле имени НЕ остаётся пустым никогда.
         Сайт на этом шаге не читается, и `found.name` — метка домена, а не
         ответ. Но пустое поле на первом экране платного продукта означает
         «ничего не работает»: человек вводит адрес, видит пустоту и уходит.
         Поэтому заготовка подставляется видимой и правимой, а честность
         обеспечивается не пустотой, а подписью поля и тем, что НЕ ТРОНУТАЯ
         заготовка не уезжает в запрос: происхождение имени остаётся `domain`,
         и разбор сайта вправе её заменить. Тронул человек — имя его, `manual`,
         и разбор к нему не притронется. Признак тронутости и решает. */
      startDraft({
        name: name.trim() || found.name,
        domain: found.domain,
        regions: found.regions ?? [],
        workspaceId: chosenWorkspace,
        questionsTarget: targetDefault,
      });
      setProposal(found);
      setStage('profile');
    } catch (cause) {
      setError(cause);
      setStage('domain');
    }
  }

  /**
   * Начать заполнение профиля с чистого листа.
   *
   * Черновики чип-полей поднялись в состояние экрана, а значит переживают
   * возврат на первый шаг: набранный и не подтверждённый город от прошлой
   * попытки иначе молча уезжает в СЛЕДУЮЩИЙ бренд — вместе с ним его добавляет
   * `chipsValue` при отправке. Сбрасываются они ровно там же, где заводится сам
   * черновик, чтобы третьего места для этого не появилось.
   */
  function startDraft(next: Draft) {
    setDraft(next);
    setRegionDraft('');
    /* Признак тронутости имени заводится ЗДЕСЬ ЖЕ, а не у каждого вызывающего.
       Пока его ставил только удачный разбор, «Заполнить руками» после отказа
       `detect` оставляло его ложным при заполненном поле: имя, набранное
       человеком на первом шаге, не уезжало в запрос, сервер записывал его как
       метку домена, и разбор сайта потом молча его заменял. */
    setNameTouched(name.trim() !== '');
  }

  /** Сайт не открылся или человек не хочет ждать: заполняем профиль руками. */
  function fillByHand() {
    setError(null);
    setProposal(null);
    startDraft({
      name: name.trim(),
      domain: normalizeDomain(domain),
      regions: [],
      workspaceId: chosenWorkspace,
      questionsTarget: targetDefault,
    });
    setStage('profile');
  }

  /** Бренд вместе с очередью подготовки: вопросы напишет воркер. */
  async function withCore(from: Draft): Promise<Created> {
    const result = await api.post<BootstrapResult>(
      '/brands/bootstrap',
      {
        workspace_id: from.workspaceId,
        // Домен обязателен: адрес сайта — единственное, из чего продукт узнаёт
        // о бизнесе хоть что-то, а без этого вопросы писать не из чего.
        domain: normalizeDomain(from.domain),
        // Имя уходит, только если его назвал человек. Заготовка по адресу
        // стоит в поле видимой — иначе экран пуст, — но в запрос не уезжает:
        // сервер записал бы её как названную человеком, и разбор сайта не
        // тронул бы её никогда. Судит признак тронутости, а не пустота поля.
        ...(nameTouched && from.name.trim() ? { name: from.name.trim() } : {}),
        regions: from.regions,
        questions_target: Number(from.questionsTarget) || null,
      },
      { idempotencyKey: idempotencyKey() },
    );
    return { kind: 'core', result };
  }

  /**
   * Один бренд, без вопросов и без замера.
   *
   * Путь организации, которой тариф ещё не даёт ни одного вопроса. Всё, что
   * человек подтвердил на втором шаге, записывается: города и варианты
   * написания названия — синонимы контракт принимает прямо в `POST /brands`.
   * Не переносятся только конкуренты: отдельной ручкой их заводят из карточки
   * бренда, и делать это молча за спиной клиента экран не станет.
   *
   * **Заглушка имени подставляется только здесь.** Имя обязательно по
   * контракту, а подготовки на этом пути нет вовсе: сайт никто не прочитает, и
   * ждать названия неоткуда. Метка домена — плохое имя, но с ним бренд хотя бы
   * заводится и правится в карточке.
   *
   * Синонимы заготовки не переносятся по той же причине, по которой не
   * переносится её имя: они выведены из адреса, а не прочитаны на сайте.
   * Канонический синоним сервер заведёт сам из названия.
   */
  async function withoutCore(from: Draft): Promise<Created> {
    const brand = await api.post<Brand>(
      '/brands',
      {
        workspace_id: from.workspaceId,
        domain: normalizeDomain(from.domain) || null,
        name: from.name.trim() || nameFromDomain(from.domain),
        regions: from.regions,
        aliases: siteRead ? (proposal?.aliases ?? []) : [],
      },
      { idempotencyKey: idempotencyKey() },
    );
    return { kind: 'brand', brand };
  }

  /**
   * @param mode `brand-only` — человек сам выбрал завести бренд без ядра.
   *
   * Выбор появляется там, где тариф прочитать не удалось: без него отказ
   * необязательного `GET` запирал заведение намертво, хотя `POST /brands`
   * тарифа не касается. Дубля здесь быть не может — `bootstrap` в этой ветке
   * не вызывается вовсе, а значит нечему остаться закоммиченным.
   */
  async function create(mode: 'auto' | 'brand-only' = 'auto') {
    if (!draft) return;
    // Недовведённые слова чип-полей уезжают вместе со всем остальным: человек
    // вписал город и нажал «Завести бренд», не подтвердив Enter-ом, — и это
    // ровно то же намерение, что и подтверждённое. Подробности — `Chips`.
    const from: Draft = { ...draft, regions: chipsValue(draft.regions, regionDraft) };
    // Снимок берётся ДО запроса: по нему потом отличается бренд, родившийся в
    // этой попытке, от давно заведённого на тот же сайт.
    knownBefore.current = new Set(scope.liveBrands.map((item) => item.id));
    setError(null);
    setStage('creating');
    try {
      const made =
        mode === 'brand-only' || !canBuildCore
          ? await withoutCore(from)
          : await withCore(from);
      scope.reload();
      scope.selectBrand(made.kind === 'core' ? made.result.brand.id : made.brand.id);
      setCreated(made);
      setStage('done');
    } catch (cause) {
      /* Повтора «не вышло с ядром — заведём без ядра» здесь нет намеренно, хотя
         он напрашивается. `bootstrap` не транзакционен: `create_brand` держит
         свою транзакцию и коммитит бренд до того, как бренд встанет в очередь
         подготовки, поэтому отказ по тарифу на следующем шаге оставляет бренд
         **уже заведённым**. Второй `POST /brands` в этот момент завёл бы клиенту
         дубль вместо помощи.

         Отказ по тарифу здесь означает гонку: место в ядре заняли, пока человек
         заполнял форму, — либо у пространства своя выделенная квота, которой
         экран не знает (её видно только через `/workspaces/{id}/allocation`).
         Область перечитывается, чтобы бренд, который всё-таки завёлся, стал
         виден в переключателе, а не пропал молча. Причину показывает сервер
         своим текстом: она точнее любой догадки экрана. */
      // Область перечитывается на ЛЮБОМ отказе, а не только на тарифном:
      // бренд мог завестись и при другом. Что с ним делать дальше, решает
      // `madeAnyway`.
      scope.reload();
      setError(cause);
      setStage('profile');
    }
  }

  /* ─────────────────────────────── шаг 1 ─────────────────────────────────── */

  if (stage === 'domain' || stage === 'detecting') {
    return (
      <>
        {head}
        {quota ? <PlanNotice quota={quota} free={free} canPay={canPay} /> : null}
        <div className="panel">
          {/* Шаг обещает ровно то, что делает СЕЙЧАС. `POST /brands/detect`
              сайт не открывает вовсе: название он выводит из адреса, города и
              конкуренты пусты. Читает сайт подготовка — после подтверждения, и
              экран называет это в правильном порядке, а не задним числом. */}
          <p className="panel-lead">
            Введите адрес сайта. Название, города и варианты написания мы возьмём с самого
            сайта — прочитаем его сразу после того, как вы подтвердите. По нему же напишем
            вопросы покупателя. Ничего не сохранится, пока вы не подтвердите.
          </p>

          {error ? (
            <ErrorNotice
              error={error}
              action={
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={fillByHand}
                >
                  Заполнить руками
                </button>
              }
            />
          ) : null}

          {stage === 'detecting' ? (
            <div className="running">
              <p className="stamp">Проверяем адрес сайта</p>
              <Pipeline steps={prepareSteps()} current={0} />
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void detect();
              }}
            >
              <TextField
                label="Адрес сайта"
                value={domain}
                onChange={setDomain}
                onBlur={() => setDomain(normalizeDomain(domain))}
                placeholder="example.ru"
                focusOnMount
                error={fields.domain ?? null}
                hint="Можно вставить адрес целиком — протокол и страницу уберём сами"
              />
              <TextField
                label="Название бренда"
                value={name}
                onChange={setName}
                optional
                placeholder="Автоцентр Прайд"
                hint="Так, как вас называют покупатели. Не заполните — возьмём название с сайта"
              />
              <div className="row-actions">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={!domain.trim()}
                >
                  Разобрать сайт
                </button>
                <button type="button" className="linkish" onClick={fillByHand}>
                  Заполнить руками
                </button>
              </div>
            </form>
          )}
        </div>
      </>
    );
  }

  /* ─────────────────────────────── готово ────────────────────────────────── */

  // Бренд заведён без ядра: тариф не дал ни одного вопроса. Экран не делает вид,
  // что всё получилось, но и не выглядит отказом — бренд действительно записан.
  if (stage === 'done' && created?.kind === 'brand') {
    const brand = created.brand;
    return (
      <>
        {head}
        <div className="panel">
          <span className="eyebrow">Бренд заведён, вопросов нет</span>
          <h2 className="h3">{brand.name}</h2>
          <p className="panel-lead">
            Сайт и всё, что вы заполнили, записано — вводить заново не придётся. Вопросов
            у бренда пока нет:{' '}
            {quota && !quota.plan_code
              ? `${CORE_SIZE_RULE} Действующего тарифа у организации ещё нет`
              : 'свободных вопросов тариф сейчас не даёт — их делят все бренды организации'}
            .
          </p>
          <p className="stamp">
            {canPay
              ? 'После оплаты вопросы напишутся по этому же сайту сами. Если этого не случилось, на экране «Семантическое ядро» есть кнопка «Пересобрать вопросы» — пока ядро пустое, пересобирать ей нечего и терять нечего.'
              : 'Вопросы соберутся, когда тариф изменит владелец организации. Профиль бренда до этого никуда не денется.'}
          </p>
          <div className="row-actions">
            {canPay ? (
              <a className="btn btn-primary" href="/plan">
                Открыть тариф
              </a>
            ) : null}
            <button
              className={canPay ? 'btn btn-ghost' : 'btn btn-primary'}
              type="button"
              onClick={() => navigate(`/brands/${brand.id}`)}
            >
              Открыть карточку бренда
            </button>
          </div>
        </div>
      </>
    );
  }

  if (stage === 'done' && created?.kind === 'core') {
    return (
      <>
        {head}
        <QuestionsPreparing
          created={created.result.brand}
          planned={created.result.questions_planned}
          onPrepared={setPrepared}
        />
      </>
    );
  }

  /* ─────────────────────────────── шаг 2 ─────────────────────────────────── */

  if (!draft) return null;

  return (
    <>
      {head}

      {quota ? <PlanNotice quota={quota} free={free} canPay={canPay} /> : null}

      {error ? <ErrorNotice error={error} /> : null}

      <div className="two-pane">
        <div className="panel">
          <span className="eyebrow">Подтвердите профиль</span>
          <p className="panel-lead">
            {!proposal
              ? 'Сайт разобрать не вышло, поэтому заполните основное сами. Вопросы мы напишем по сайту, а города задают их географию.'
              : siteRead
                ? 'Это то, что мы прочитали на сайте. Поправьте, если ошиблись: по этому профилю мы и напишем вопросы покупателя.'
                : siteWillTell
                  ? // Название в поле — догадка по адресу, и названа она так
                    // вслух. Сайт читает подготовка, сразу после заведения:
                    // тогда же название уточнится и появятся города. Обещание
                    // здесь честное ровно потому, что названо время.
                    'Название мы предположили по адресу сайта. Сам сайт прочитаем сразу после заведения — тогда уточним название и найдём города. Впишите своё, если наша догадка мимо: своё мы уже не тронем.'
                  : // Сайт читает подготовка, а она идёт вместе с вопросами.
                    // Вопросов сейчас не будет, значит и сайт прочитать некому:
                    // обещать разбор на этом пути нельзя.
                    'Вопросы сейчас не собираются, а сайт мы читаем вместе с ними — поэтому название и города заполните сами. Поправить их можно в карточке бренда.'}
          </p>

          {stage === 'creating' ? (
            <div className="running">
              <p className="stamp">Заводим бренд</p>
              <Pipeline steps={prepareSteps()} current={0} />
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void create();
              }}
            >
              <TextField
                label="Название бренда"
                value={draft.name}
                onChange={(value) => {
                  setNameTouched(true);
                  setDraft({ ...draft, name: value });
                }}
                optional={siteWillTell}
                error={fields.name ?? null}
                hint={
                  siteWillTell && !nameTouched
                    ? 'Предположили по адресу сайта. Оставьте как есть — уточним по самому сайту; впишите своё — не тронем'
                    : 'Так, как вас называют покупатели, а не как записано в уставе'
                }
              />

              <TextField
                label="Сайт"
                value={draft.domain}
                onChange={(value) => setDraft({ ...draft, domain: value })}
                onBlur={() =>
                  setDraft({ ...draft, domain: normalizeDomain(draft.domain) })
                }
                error={fields.domain ?? null}
                hint="По нему мы пишем вопросы покупателя и считаем, ссылаются ли на вас нейросети"
              />

              <Chips
                label="Города и регионы"
                values={draft.regions}
                onChange={(regions) => setDraft({ ...draft, regions })}
                draft={regionDraft}
                onDraftChange={setRegionDraft}
                placeholder="Казань, Набережные Челны"
                hint={
                  siteWillTell
                    ? 'Оставьте пустым — найдём города на сайте сразу после заведения. Впишите свои, только если хотите сузить географию'
                    : 'Покупатель спрашивает нейросеть про свой город — без этого вопросы получатся общими'
                }
              />

              {/* Предвыбрано то пространство, в котором человек находится, а не
                  первое в списке (`defaultWorkspaceId`). При единственном
                  пространстве поля нет: выбирать не из чего, а контрол с одним
                  вариантом — это вопрос, на который есть один ответ. */}
              {workspaces.length > 1 ? (
                <SelectField
                  label="Рабочее пространство"
                  value={draft.workspaceId}
                  onChange={(value) => setDraft({ ...draft, workspaceId: value })}
                  options={workspaces.map((w) => ({
                    value: w.id,
                    label: w.client_display_name ?? w.name,
                  }))}
                  hint="Бренд и его вопросы уйдут сюда, и квоту он возьмёт отсюда же"
                />
              ) : null}

              {/* Размер ядра спрашивается только тогда, когда тариф даёт хоть
                  один вопрос. Без него контрол ничего не делает, а по
                  app-kit.md §1 нерабочих контролов на экране не рисуют —
                  состояние объясняет заметка над формой. */}
              {canBuildCore ? (
                <TextField
                  label="Сколько вопросов отслеживать"
                  value={draft.questionsTarget}
                  onChange={(value) =>
                    setDraft({ ...draft, questionsTarget: value.replace(/\D/g, '') })
                  }
                  type="number"
                  inputMode="numeric"
                  error={fields.questions_target ?? null}
                  hint={
                    quota
                      ? `Тариф даёт ${countOf(quota.tracked_questions, 'отслеживаемый вопрос', 'отслеживаемых вопроса', 'отслеживаемых вопросов')} на всю организацию, свободно ${int(free ?? 0)}`
                      : CORE_SIZE_RULE
                  }
                />
              ) : null}

              {/* Ждёт только кнопка, а не форма: профиль заполняется, пока
                  тариф читается. Сам ответ нужен ровно здесь — им выбирается
                  запрос, и ошибиться в выборе дороже, чем подождать. */}
              {/* Одна полоса на оба состояния, и она не исчезает от нажатия.
                  Раньше «Повторить» удаляло само себя вместе с полосой: фокус
                  падал на body, а смена состояния не объявлялась вовсе. Теперь
                  это постоянная live-область — кнопка остаётся на месте, а
                  текст под ней меняется и зачитывается. */}
              {!quotaKnown ? (
                <div className="notice warn" role="status">
                  <b>
                    {quotas.state.kind === 'loading'
                      ? 'Читаем тариф'
                      : 'Не удалось прочитать тариф'}
                  </b>
                  {quotas.state.kind === 'loading'
                    ? 'От тарифа зависит, соберутся ли вопросы вместе с брендом, поэтому кнопка заведения ждёт ответа. Заполненное никуда не денется.'
                    : 'От тарифа зависит, соберутся ли вопросы вместе с брендом. Можно прочитать тариф ещё раз или завести бренд сейчас — но тогда сайт никто не прочитает, и название придётся вписать самим: вопросы и разбор придут позже.'}
                  <span className="notice-act">
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={quotas.reload}
                    >
                      {quotas.state.kind === 'loading' ? 'Читаем…' : 'Повторить'}
                    </button>
                    {/* Этот путь идёт мимо подготовки, а значит мимо разбора
                        сайта: имя тут больше взять неоткуда, и без него сервер
                        записал бы метку домена — навсегда, как названную
                        человеком. Поэтому кнопка ждёт заполненного названия. */}
                    {quotas.state.kind === 'failed' ? (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        disabled={!draft.domain.trim()}
                        onClick={() => void create('brand-only')}
                      >
                        Завести только бренд
                      </button>
                    ) : null}
                  </span>
                </div>
              ) : null}

              <div className="row-actions">
                {/* Адрес сайта обязателен на обоих путях заведения: по нему
                    пишутся вопросы, и `POST /brands/bootstrap` без него
                    отвечает `422`. Название обязательно не всегда: на заготовке
                    его определит разбор сайта, и требовать его от человека
                    значит требовать выдумать ответ за нас. */}
                {/* Имя кнопку больше не гейтит.
                    Поле заполнено заготовкой всегда, а если человек её стёр —
                    заглушку подставит сервер (`bootstrap`) или `nameFromDomain`
                    (`withoutCore`). Держать кнопку мёртвой из-за поля, которое
                    не помечено обязательным и ничего не объясняет, значит
                    показывать человеку экран без выхода: ровно это и случилось
                    у первого же клиента. */}
                {/* Заведение УБИРАЕТСЯ, когда бренд этой попытки уже нашёлся:
                    рядом стоящая живая кнопка «Завести бренд» — это приглашение
                    сделать дубль, а объяснение отказа человек уже прочитал. */}
                {madeAnyway ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      scope.selectBrand(madeAnyway.id);
                      setCreated({ kind: 'brand', brand: madeAnyway });
                      setStage('done');
                    }}
                  >
                    Открыть заведённый бренд
                  </button>
                ) : (
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={!draft.domain.trim() || !quotaKnown}
                  >
                    Завести бренд
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setStage('domain')}
                >
                  Назад
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Панель находок показывается только тогда, когда находки есть.
            Заготовка держала в ней имя, выведенное из адреса, пустых
            конкурентов и описание отрасли из справочника — три утверждения о
            бизнесе, ни одно из которых с сайта не прочитано. Рядом с полями,
            которые честно говорят «возьмём с сайта», это был спор экрана с
            самим собой. */}
        {proposal && siteRead ? (
          <div className="panel">
            <span className="eyebrow">Нашли на сайте</span>

            <h3 className="panel-h">Варианты написания названия</h3>
            <p className="stamp">
              {ORGANIC_RULE} Сравнение по одному каноническому имени пропускает сокращение
              и латиницу — и завышает видимость.
            </p>
            <ul className="finds">
              {(proposal.aliases ?? []).map((alias) => (
                <li key={alias.alias}>
                  <b>{alias.alias}</b>
                  <span className="stamp">{aliasKind(alias.kind)}</span>
                </li>
              ))}
              {(proposal.aliases ?? []).length === 0 ? (
                <li className="stamp">
                  Ничего не нашли — добавьте варианты в карточке бренда
                </li>
              ) : null}
            </ul>

            <h3 className="panel-h">Прямые конкуренты</h3>
            <p className="stamp">
              Их видимость считается по той же сетке, что и ваша, — иначе линии на графике
              несопоставимы. Список правится в карточке бренда.
            </p>
            <ul className="finds">
              {(proposal.competitors ?? []).map((competitor) => (
                <li key={competitor.name}>
                  <b>{competitor.name}</b>
                  <span className="stamp">{competitor.domain ?? 'сайт не нашли'}</span>
                </li>
              ))}
              {(proposal.competitors ?? []).length === 0 ? (
                <li className="stamp">
                  Ничего не нашли — добавьте вручную после заведения
                </li>
              ) : null}
            </ul>

            {proposal.description ? (
              <>
                <h3 className="panel-h">Чем занимается</h3>
                <p className="panel-lead">{proposal.description}</p>
              </>
            ) : null}

            {quota ? (
              <p className="stamp">
                Занято{' '}
                {coverageOf(
                  quota.questions_used ?? 0,
                  quota.tracked_questions,
                  'вопрос',
                  'вопроса',
                  'вопросов',
                )}{' '}
                тарифа
              </p>
            ) : null}
          </div>
        ) : (
          /* Находок ещё нет — но половина экрана пустой оставаться не должна.
             Раньше здесь не рисовалось ничего, и человек видел форму, прижатую
             к левому краю, рядом с пустотой: экран выглядел недогрузившимся
             ровно там, где ему надо было объяснить, чего ждать. Вместо находок
             показывается порядок: что мы сделаем и в каком порядке. */
          <div className="panel">
            <span className="eyebrow">Что будет дальше</span>
            <p className="panel-lead">
              {siteWillTell
                ? 'Как только вы заведёте бренд, мы откроем сайт и прочитаем его — сами, без вашего участия.'
                : 'Вопросы сейчас не собираются, поэтому сайт читать некому. Всё, что вы впишете, попадёт в карточку бренда как есть.'}
            </p>
            {siteWillTell ? (
              <ul className="finds">
                <li>
                  <b>Уточним название</b>
                  <span className="stamp">
                    То, что стоит на вывеске, а не метка адреса
                  </span>
                </li>
                <li>
                  <b>Найдём города</b>
                  <span className="stamp">По адресам и телефонам на самом сайте</span>
                </li>
                <li>
                  <b>Соберём варианты написания</b>
                  <span className="stamp">
                    Сокращения, латиница, юрлицо и имя головной сети
                  </span>
                </li>
                <li>
                  <b>Напишем вопросы покупателя</b>
                  <span className="stamp">{ORGANIC_RULE}</span>
                </li>
              </ul>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Бренд заведён, набор вопросов пишется.
 *
 * Сводки «ядро собрано: N вопросов» здесь больше нет, и это не упрощение:
 * `POST /brands/bootstrap` отвечает до того, как появится хоть один вопрос, и
 * чисел о ядре в ответе нет вовсе. Показывать вместо них нули значило бы
 * отчитаться о наборе, которого ещё не существует.
 *
 * Ход подготовки виден только опросом бренда, а её конец — единственная минута,
 * когда человеку есть что показать: до неё бренд назван заглушкой и живёт без
 * городов, после — назван тем, что стоит на вывеске сайта. Поэтому готовый
 * бренд экран НЕ пролистывает в карточку молча, а называет вслух: название,
 * города, синонимы — включая головную сеть, о которой человек не просил и
 * которая теперь считается за упоминание.
 */
function QuestionsPreparing({
  created,
  planned,
  onPrepared,
}: {
  created: Brand;
  planned: number;
  /** Готовность наверх, шапке мастера. Ставится сеттером состояния — он
   *  постоянен, и эффект от него не перезапускается. */
  onPrepared: (done: boolean) => void;
}) {
  const navigate = useNavigate();
  const [brand, setBrand] = useState(created);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const watch = usePreparation(isPreparing(brand) ? brand.id : null, setBrand);

  const ready = brand.preparation_state === 'ready';

  /* `idle` — не ожидание: так помечен бренд, над которым подготовка не
     выполнялась вовсе. Разбор сайта его не касался, показывать как найденное
     нечего, и экран уводит в карточку — замена в истории, чтобы «назад» не
     возвращало в законченный мастер. */
  const nothingPrepared = brand.preparation_state === 'idle';

  useEffect(() => {
    if (nothingPrepared) navigate(`/brands/${brand.id}`, { replace: true });
  }, [nothingPrepared, brand.id, navigate]);

  useEffect(() => {
    onPrepared(ready);
  }, [ready, onPrepared]);

  /* Синонимов в бренде нет, они приезжают своей ручкой. Ключ меняется вместе с
     готовностью, поэтому до конца подготовки запрос не уходит вовсе. */
  const aliases = useLoad<BrandAlias[] | null>(
    `prepared-aliases:${brand.id}:${String(ready)}`,
    (signal) =>
      ready
        ? api.get<BrandAlias[]>(`/brands/${brand.id}/aliases`, { signal })
        : Promise.resolve(null),
  );

  const attempt = useRebuildAttempt();

  async function retry() {
    setRetrying(true);
    setError(null);
    try {
      setBrand(await rebuildQuestions(brand.id, attempt.key));
      attempt.renew();
    } catch (cause) {
      setError(cause);
    } finally {
      setRetrying(false);
    }
  }

  if (ready) {
    const found = aliases.state.kind === 'ready' ? (aliases.state.data ?? []) : [];
    const regions = brand.regions ?? [];
    return (
      <div className="panel">
        <span className="eyebrow">Бренд готов</span>
        <h2 className="h3">{brand.name}</h2>
        <p className="panel-lead">
          Вопросы написаны, профиль собран. Вот с чем бренд заведён. Название и города
          можно поправить прямо здесь, остальное — в карточке. Первый замер запускается
          вручную, в разделе «Прогоны».
        </p>

        <ul className="finds">
          {/* Источник имени назван вслух. Без него смена названия выглядит
              самоуправством продукта, а НЕсмена — поломкой разбора: и то и
              другое человек объясняет себе сам, и объясняет неверно. */}
          <li>
            <b>{brand.name}</b>
            <span className="stamp">
              {brand.name_source ? nameSource(brand.name_source) : 'Название бренда'}
            </span>
          </li>
          <li>
            <b>{brand.domain ? domainProse(brand.domain) : 'сайт не добавлен'}</b>
            <span className="stamp">По нему написаны вопросы</span>
          </li>
          <li>
            <b>{regions.length > 0 ? regions.join(' · ') : 'города не нашли'}</b>
            <span className="stamp">
              {regions.length > 0
                ? 'География вопросов'
                : 'Впишите их сами — вопросы получились общими'}
            </span>
          </li>
        </ul>

        <FixOnTheSpot brand={brand} onSaved={setBrand} />

        <h3 className="panel-h">Варианты написания названия</h3>
        {aliases.state.kind === 'loading' ? (
          <Skeleton rows={1} />
        ) : aliases.state.kind === 'failed' ? (
          <p className="stamp">Синонимы не открылись — они есть в карточке бренда</p>
        ) : (
          <>
            <ul className="finds">
              {found.map((alias) => (
                <li key={alias.id}>
                  <b>{alias.alias}</b>
                  <span className="stamp">{aliasKind(alias.kind)}</span>
                </li>
              ))}
              {found.length === 0 ? (
                <li className="stamp">
                  Ничего не нашли — добавьте варианты в карточке бренда
                </li>
              ) : null}
            </ul>
            {found.some((alias) => alias.kind === 'parent') ? (
              <p className="stamp">{parentAliasNote()}</p>
            ) : null}
          </>
        )}

        <div className="row-actions">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => navigate(`/brands/${brand.id}`)}
          >
            Открыть карточку бренда
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <span className="eyebrow">Бренд заведён</span>
      <h2 className="h3">{brand.name}</h2>
      <p className="panel-lead">
        Профиль записан. Вопросы покупателя пишутся по вашему сайту — как только набор
        будет готов, мы покажем, что прочитали. Первый замер запускается вручную, в
        разделе «Прогоны». Дальше замеры идут сами, каждую неделю.
      </p>

      {watch.error ? <ErrorNotice error={watch.error} /> : null}
      {error ? <ErrorNotice error={error} /> : null}

      {brand.preparation_state === 'failed' ? (
        <PreparationFailed
          note={brand.preparation_note}
          busy={retrying}
          onRetry={() => void retry()}
        />
      ) : (
        <Preparing planned={planned} />
      )}

      <div className="row-actions">
        <button
          className="btn btn-ghost"
          type="button"
          onClick={() => navigate(`/brands/${brand.id}`)}
        >
          Открыть карточку бренда
        </button>
      </div>
    </div>
  );
}

/**
 * Поправить название и города, не уходя с экрана готовности.
 *
 * Разбор сайта — догадка машины, и первый человек, который может её проверить,
 * стоит именно здесь: он только что увидел, что мы прочитали. Отправлять его за
 * правкой в карточку значит терять единственную минуту, когда он на это смотрит.
 *
 * Свёрнуто по умолчанию: у большинства разбор попадает, и раскрытая форма
 * превращала бы удачный исход в работу. Открывается по своей же надписи.
 *
 * Правка ставит `name_source = manual` на стороне сервера, и это правильно:
 * человек поправил разбор, и следующий разбор его правку затирать не вправе.
 */
/* Один идентификатор на кнопку и форму: `aria-controls` связывает их, а
   `aria-expanded` называет состояние. Экран мастера показывает эту форму в
   единственном экземпляре, поэтому постоянной строки достаточно. */
const FIX_FORM_ID = 'fix-on-the-spot';

function FixOnTheSpot({
  brand,
  onSaved,
}: {
  brand: Brand;
  onSaved: (brand: Brand) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(brand.name);
  const [regions, setRegions] = useState<string[]>(brand.regions ?? []);
  const [regionDraft, setRegionDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /* Раскрытие и закрытие УНОСЯТ НАЖАТУЮ КНОПКУ ИЗ РАЗМЕТКИ, и без переноса
     фокуса тот, кто пришёл сюда с клавиатуры, теряет место: браузер отдаёт
     фокус в начало документа, а читалка молчит о том, что вообще что-то
     произошло. Поэтому фокус ведётся руками — в первое поле формы при
     раскрытии и обратно на кнопку при закрытии, — а исход правки объявляется
     областью `role="status"`. */
  const opener = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef(false);

  useEffect(() => {
    if (open || !returnFocus.current) return;
    opener.current?.focus();
    returnFocus.current = false;
  }, [open]);

  const problem = error instanceof ApiError ? error.problem : null;
  const fields = problem ? fieldErrors(problem) : {};

  async function save() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const next = await api.patch<Brand>(`/brands/${brand.id}`, {
        name: name.trim(),
        regions: chipsValue(regions, regionDraft),
      });
      onSaved(next);
      /* Поля берутся ИЗ ОТВЕТА, а не остаются как были. В запрос уходит
         недовведённое слово чип-поля («Химки» без Enter), и сервер вправе
         привести список к своему виду; без этой строки повторное раскрытие
         формы показывало бы прежний список и стирало бы сохранённое следующей
         же правкой. */
      setName(next.name);
      setRegions(next.regions ?? []);
      setRegionDraft('');
      setSaved(true);
      returnFocus.current = true;
      setOpen(false);
    } catch (cause) {
      setError(cause);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <p className="stamp">
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          ref={opener}
          aria-expanded={false}
          aria-controls={FIX_FORM_ID}
          onClick={() => setOpen(true)}
        >
          Поправить название или города
        </button>
        <span role="status">{saved ? ' Сохранено' : ''}</span>
      </p>
    );
  }

  return (
    <form
      id={FIX_FORM_ID}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      {error ? <ErrorNotice error={error} /> : null}

      <TextField
        label="Название бренда"
        value={name}
        onChange={setName}
        focusOnMount
        error={fields.name ?? null}
        hint="Так, как вас называют покупатели. Ваша правка разбором больше не перетирается"
      />

      <Chips
        label="Города и регионы"
        values={regions}
        onChange={setRegions}
        draft={regionDraft}
        onDraftChange={setRegionDraft}
        placeholder="Москва, Химки"
        hint="Покупатель спрашивает нейросеть про свой город — без этого вопросы получаются общими"
      />

      <div className="row-actions">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={saving || !name.trim()}
        >
          {saving ? 'Сохраняем' : 'Сохранить'}
        </button>
        <button
          className="btn btn-ghost"
          type="button"
          onClick={() => {
            returnFocus.current = true;
            setOpen(false);
            setName(brand.name);
            setRegions(brand.regions ?? []);
            setRegionDraft('');
            setError(null);
          }}
        >
          Отмена
        </button>
      </div>
    </form>
  );
}

/**
 * Тариф — заметка над формой, а не стена вместо неё.
 *
 * Образец — экран тарифа: «Замеры остановлены» стоит там полосой над панелями и
 * ничего не закрывает. Здесь то же правило и по той же причине: заведение бренда
 * оплаты не требует (`POST /brands` не гейтится), а требует её только ядро и
 * замер. Заметка называет ровно то, что не сработает, и оставляет выход.
 *
 * Возвращает `null`, когда тариф в порядке: полоса «всё хорошо» — это шум.
 */
function PlanNotice({
  quota,
  free,
  canPay,
}: {
  quota: Quotas;
  free: number | null;
  canPay: boolean;
}) {
  const noRoom = free !== null && free <= 0;
  if (quota.scan_allowed && !noRoom) return null;

  /* Тарифа нет вовсе — это `plan_code`, а не `scan_allowed`. Флаг замеров
     снимается ещё и приостановкой, и перерасходом (контракт, `scan_allowed`):
     выводить из него «оплатите» значит говорить действующему клиенту, что у
     него нет тарифа. Наличие тарифа и вместимость ядра — разные факты, и
     дальше они разведены. */
  const noPlan = !quota.plan_code;

  return (
    <div className="notice warn" role="status">
      <b>
        {noPlan
          ? 'Замеры начнутся после оплаты'
          : noRoom
            ? 'Свободных вопросов в тарифе нет'
            : 'Замеры остановлены'}
      </b>
      {noPlan ? (
        <>
          Бренд заводится сейчас — для этого тариф не нужен. Ядро вопросов и сами замеры
          включатся, когда тариф станет действующим: его размер определяет, сколько
          вопросов мы отслеживаем.
        </>
      ) : noRoom ? (
        <>
          Отслеживается{' '}
          {coverageOf(
            quota.questions_used ?? 0,
            quota.tracked_questions,
            'вопрос',
            'вопроса',
            'вопросов',
          )}{' '}
          тарифа — вопросы делят все бренды организации, а не каждый бренд отдельно. Бренд
          заведётся, но вопросы под него не соберутся, пока не освободится место или не
          вырастет тариф.
        </>
      ) : (
        <>
          {quota.scan_blocked_reason ?? scanBlockedFallback()} Бренд и вопросы заводятся
          как обычно, а первый замер придётся запустить после возобновления.
        </>
      )}
      {canPay ? (
        <span className="notice-act">
          <a className="btn btn-ghost btn-sm" href="/plan">
            Открыть тариф
          </a>
        </span>
      ) : (
        <span className="stamp">Изменить тариф может владелец организации.</span>
      )}
    </div>
  );
}
